import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Separator } from '../../components/ui/separator';
import {
  DollarSign, Users, TrendingUp, Clock, CheckCircle2, XCircle, AlertCircle,
  Search, Download, Plus, Edit, Trash2, Eye, RefreshCw, Calendar,
  CreditCard, Banknote, Building2, Loader2, ChevronLeft, ChevronRight,
  Package, Layers, FileText, Hotel, Route, Settings, BarChart3, Send,
  UserPlus, Percent, FileSpreadsheet, Database, Shield, Receipt, Check
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = {
  get: async (url) => {
    const response = await fetch(`${API_URL}/api/v1${url}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` }
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'API Error');
    }
    return response.json();
  },
  post: async (url, data) => {
    const response = await fetch(`${API_URL}/api/v1${url}`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` 
      },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'API Error');
    }
    return response.json();
  },
  put: async (url, data) => {
    const response = await fetch(`${API_URL}/api/v1${url}`, {
      method: 'PUT',
      headers: { 
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` 
      },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'API Error');
    }
    return response.json();
  },
  delete: async (url) => {
    const response = await fetch(`${API_URL}/api/v1${url}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` }
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'API Error');
    }
    return response.json();
  }
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount / 100);
};

// Feature categories for grouping in UI
const FEATURE_CATEGORIES = {
  quotation_types: { name: 'Quotation Types', order: 1 },
  dmc_features: { name: 'DMC Features', order: 2 },
  limits: { name: 'Usage Limits', order: 3 },
  pricing: { name: 'Pricing Features', order: 4 },
  output: { name: 'Output & Dispatch', order: 5 },
  advanced: { name: 'Advanced Features', order: 6 },
  core: { name: 'Core', order: 0 },
};

// Feature keys available in the system
const ALL_FEATURES = [
  // Core
  { key: 'quotation_engine', name: 'Quotation Engine', hasLimit: false, icon: Package, category: 'core' },
  
  // Quotation Types (NEW)
  { key: 'quote_type_predefined', name: 'Predefined Routes', hasLimit: false, icon: Route, category: 'quotation_types', isNew: true },
  { key: 'quote_type_custom', name: 'Custom Package', hasLimit: false, icon: Package, category: 'quotation_types', isNew: true },
  { key: 'quote_type_manual', name: 'Manual Package', hasLimit: false, icon: FileText, category: 'quotation_types', isNew: true },
  { key: 'quote_type_dmc', name: 'DMC Package', hasLimit: false, icon: Building2, category: 'quotation_types', isNew: true },
  
  // DMC Features (NEW)
  { key: 'dmc_ai_parse', name: 'DMC AI Parsing', hasLimit: false, icon: Settings, category: 'dmc_features', isNew: true },
  { key: 'dmc_file_upload', name: 'DMC File Upload', hasLimit: false, icon: FileText, category: 'dmc_features', isNew: true },
  
  // Usage Limits
  { key: 'quotes_per_month', name: 'Quotations per Month', hasLimit: true, period: 'monthly', icon: FileText, category: 'limits' },
  { key: 'hotel_slots', name: 'Hotel Slots', hasLimit: true, period: 'total', icon: Hotel, category: 'limits' },
  { key: 'b2b_agent_portal', name: 'B2B Agent Portal', hasLimit: true, period: 'total', icon: UserPlus, category: 'limits' },
  { key: 'staff_logins', name: 'Staff Logins', hasLimit: true, period: 'total', icon: Users, category: 'limits' },
  { key: 'route_slots', name: 'Route Slots', hasLimit: true, period: 'total', icon: Route, category: 'limits' },
  
  // Pricing Features
  { key: 'multi_category', name: 'Multi-Category Pricing', hasLimit: false, icon: Layers, category: 'pricing' },
  { key: 'dual_meal_plan', name: 'Dual Meal Plan', hasLimit: false, icon: Percent, category: 'pricing' },
  { key: 'person_wise_format', name: 'Person-wise Format', hasLimit: false, icon: Users, category: 'pricing' },
  { key: 'misc_cost_lines', name: 'Misc Cost Lines', hasLimit: false, icon: Database, category: 'pricing' },
  { key: 'gst_configuration', name: 'GST Configuration', hasLimit: false, icon: Settings, category: 'pricing' },
  
  // Output & Dispatch
  { key: 'whatsapp_dispatch', name: 'WhatsApp Dispatch', hasLimit: false, icon: Send, category: 'output' },
  { key: 'auto_doc_generation', name: 'Auto Doc Generation', hasLimit: false, icon: FileText, category: 'output' },
  { key: 'quote_terms', name: 'Quote Terms Builder', hasLimit: false, icon: FileText, category: 'output' },
  
  // Advanced Features
  { key: 'transfer_manager', name: 'Transfer Manager', hasLimit: false, icon: Route, category: 'advanced' },
  { key: 'season_aware_rates', name: 'Season Aware Rates', hasLimit: false, icon: Calendar, category: 'advanced' },
  { key: 'itinerary_master', name: 'Itinerary Master', hasLimit: false, icon: FileSpreadsheet, category: 'advanced' },
  { key: 'pipeline_dashboard', name: 'Pipeline Dashboard', hasLimit: false, icon: BarChart3, category: 'advanced' },
  { key: 'hotels_by_category', name: 'Hotels by Category', hasLimit: false, icon: Hotel, category: 'advanced' },
];

// Group features by category
const getFeaturesByCategory = () => {
  const grouped = {};
  ALL_FEATURES.forEach(feature => {
    const cat = feature.category || 'core';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(feature);
  });
  
  // Sort categories by order
  const sortedCategories = Object.keys(grouped).sort(
    (a, b) => (FEATURE_CATEGORIES[a]?.order || 99) - (FEATURE_CATEGORIES[b]?.order || 99)
  );
  
  return sortedCategories.map(cat => ({
    category: cat,
    name: FEATURE_CATEGORIES[cat]?.name || cat,
    features: grouped[cat]
  }));
};

const SubscriptionManagement = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [plans, setPlans] = useState([]);
  const [tenantSubscriptions, setTenantSubscriptions] = useState([]);
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTable, setLoadingTable] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialogs
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null);
  const [approvingPayment, setApprovingPayment] = useState(false);
  
  // Plan form state
  const [planForm, setPlanForm] = useState({
    name: '',
    slug: '',
    description: '',
    price_monthly: '',
    price_annual: '',
    trial_available: true,
    trial_durations: [7, 14],
    is_public: true,
    is_active: true,
    sort_order: 1,
    features: {}
  });

  // Invoice form state
  const [invoiceForm, setInvoiceForm] = useState({
    tenant_id: '',
    plan_id: '',
    billing_cycle: 'monthly',
    notes: ''
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (activeTab === 'tenants') {
      fetchTenantSubscriptions();
    }
  }, [activeTab, statusFilter]);

  const fetchInitialData = async () => {
    try {
      const [plansData, tenantsData, invoicesData] = await Promise.all([
        api.get('/admin/plans?include_inactive=true'),
        api.get('/admin/tenants/subscriptions'),
        api.get('/admin/invoices?status=pending')
      ]);
      setPlans(plansData);
      setTenantSubscriptions(tenantsData);
      setPendingInvoices(invoicesData || []);
    } catch (error) {
      toast.error('Failed to load data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTenantSubscriptions = async () => {
    setLoadingTable(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      const data = await api.get(`/admin/tenants/subscriptions?${params.toString()}`);
      setTenantSubscriptions(data);
    } catch (error) {
      toast.error('Failed to load tenant subscriptions');
    } finally {
      setLoadingTable(false);
    }
  };

  const openCreatePlanDialog = () => {
    setEditingPlan(null);
    setPlanForm({
      name: '',
      slug: '',
      description: '',
      price_monthly: '',
      price_annual: '',
      trial_available: true,
      trial_durations: [7, 14],
      is_public: true,
      is_active: true,
      sort_order: plans.length + 1,
      features: {}
    });
    setShowPlanDialog(true);
  };

  const openEditPlanDialog = (plan) => {
    setEditingPlan(plan);
    const featuresMap = {};
    plan.features?.forEach(f => {
      featuresMap[f.feature_key] = {
        is_enabled: f.is_enabled,
        limit_value: f.limit_value,
        limit_period: f.limit_period
      };
    });
    
    setPlanForm({
      name: plan.name,
      slug: plan.slug,
      description: plan.description || '',
      price_monthly: plan.price_monthly / 100,
      price_annual: plan.price_annual / 100,
      trial_available: plan.trial_available,
      trial_durations: plan.trial_durations || [7, 14],
      is_public: plan.is_public,
      is_active: plan.is_active,
      sort_order: plan.sort_order || 1,
      features: featuresMap
    });
    setShowPlanDialog(true);
  };

  const handleSavePlan = async () => {
    try {
      const featuresArray = Object.entries(planForm.features)
        .filter(([_, value]) => value.is_enabled)
        .map(([key, value]) => ({
          feature_key: key,
          is_enabled: true,
          limit_value: value.limit_value || null,
          limit_period: value.limit_period || null
        }));

      const payload = {
        name: planForm.name,
        slug: planForm.slug,
        description: planForm.description,
        price_monthly: Math.round(parseFloat(planForm.price_monthly) * 100),
        price_annual: Math.round(parseFloat(planForm.price_annual) * 100),
        trial_available: planForm.trial_available,
        trial_durations: planForm.trial_durations,
        is_public: planForm.is_public,
        is_active: planForm.is_active,
        sort_order: planForm.sort_order,
        features: featuresArray
      };

      if (editingPlan) {
        await api.put(`/admin/plans/${editingPlan.id}`, payload);
        toast.success('Plan updated successfully');
      } else {
        await api.post('/admin/plans', payload);
        toast.success('Plan created successfully');
      }
      
      setShowPlanDialog(false);
      fetchInitialData();
    } catch (error) {
      toast.error(error.message || 'Failed to save plan');
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!confirm('Are you sure you want to delete this plan?')) return;
    try {
      await api.delete(`/admin/plans/${planId}`);
      toast.success('Plan deleted');
      fetchInitialData();
    } catch (error) {
      toast.error(error.message || 'Failed to delete plan');
    }
  };

  const openInvoiceDialog = (tenant) => {
    setSelectedTenant(tenant);
    setInvoiceForm({
      tenant_id: tenant.tenant_id,
      plan_id: plans.find(p => p.is_active)?.id || '',
      billing_cycle: 'monthly',
      notes: ''
    });
    setShowInvoiceDialog(true);
  };

  const handleCreateInvoice = async () => {
    try {
      const result = await api.post('/admin/invoices', invoiceForm);
      toast.success(`Invoice ${result.invoice_number} created`);
      setShowInvoiceDialog(false);
      fetchTenantSubscriptions();
    } catch (error) {
      toast.error(error.message || 'Failed to create invoice');
    }
  };

  const openTenantDetail = (tenant) => {
    setSelectedTenant(tenant);
    setShowDetailDialog(true);
  };

  const openApproveDialog = async (invoice) => {
    // Fetch full invoice details to get payment notes
    try {
      const invoiceDetails = await api.get(`/admin/invoices/${invoice.id}`);
      setSelectedInvoice(invoiceDetails);
      setShowApproveDialog(true);
    } catch (error) {
      toast.error('Failed to load invoice details');
    }
  };

  const handleApprovePayment = async () => {
    if (!selectedInvoice) return;
    
    setApprovingPayment(true);
    try {
      await api.post('/admin/payments', {
        invoice_id: selectedInvoice.id,
        amount: selectedInvoice.total_amount,
        payment_method: 'bank_transfer',
        notes: 'Payment verified and approved by admin'
      });
      
      toast.success('Payment approved! Subscription activated.');
      setShowApproveDialog(false);
      setSelectedInvoice(null);
      
      // Refresh data
      await fetchInitialData();
    } catch (error) {
      toast.error(error.message || 'Failed to approve payment');
    } finally {
      setApprovingPayment(false);
    }
  };

  const openApproveForTenant = async (tenant) => {
    // Find the most recent pending invoice for this tenant
    const tenantInvoice = pendingInvoices.find(inv => inv.tenant_id === tenant.tenant_id);
    if (tenantInvoice) {
      await openApproveDialog(tenantInvoice);
    } else {
      toast.error('No pending invoice found for this tenant');
    }
  };

  const toggleFeature = (featureKey, hasLimit) => {
    setPlanForm(prev => {
      const features = { ...prev.features };
      if (features[featureKey]?.is_enabled) {
        delete features[featureKey];
      } else {
        features[featureKey] = {
          is_enabled: true,
          limit_value: hasLimit ? 10 : null,
          limit_period: hasLimit ? 'total' : null
        };
      }
      return { ...prev, features };
    });
  };

  const updateFeatureLimit = (featureKey, value) => {
    setPlanForm(prev => ({
      ...prev,
      features: {
        ...prev.features,
        [featureKey]: {
          ...prev.features[featureKey],
          limit_value: value === '' ? null : (value === '-1' ? -1 : parseInt(value))
        }
      }
    }));
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-700',
      trial: 'bg-blue-100 text-blue-700',
      pending: 'bg-orange-100 text-orange-700',
      past_due: 'bg-red-100 text-red-700',
      cancelled: 'bg-slate-100 text-slate-700',
      no_subscription: 'bg-yellow-100 text-yellow-700'
    };
    return <Badge className={styles[status] || 'bg-slate-100'}>{status?.replace('_', ' ') || 'None'}</Badge>;
  };

  // Calculate stats
  const stats = {
    total_tenants: tenantSubscriptions.length,
    active_subscriptions: tenantSubscriptions.filter(t => t.status === 'active').length,
    trials: tenantSubscriptions.filter(t => t.status === 'trial').length,
    pending_payments: pendingInvoices.length,
    no_subscription: tenantSubscriptions.filter(t => t.status === 'no_subscription').length
  };

  const filteredTenants = tenantSubscriptions.filter(t => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return t.company_name?.toLowerCase().includes(search) || 
             t.email?.toLowerCase().includes(search);
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="subscription-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Subscription Management</h1>
          <p className="text-slate-500">Manage plans, tenants, and billing</p>
        </div>
        <Button onClick={openCreatePlanDialog}>
          <Plus className="w-4 h-4 mr-2" />
          Create Plan
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="plans">Plans</TabsTrigger>
          <TabsTrigger value="tenants">Tenant Subscriptions</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6 mt-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Total Tenants</p>
                    <p className="text-3xl font-bold text-slate-900">{stats.total_tenants}</p>
                  </div>
                  <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-slate-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Active Subscriptions</p>
                    <p className="text-3xl font-bold text-green-600">{stats.active_subscriptions}</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Active Trials</p>
                    <p className="text-3xl font-bold text-blue-600">{stats.trials}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Clock className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Pending Payments</p>
                    <p className="text-3xl font-bold text-orange-600">{stats.pending_payments}</p>
                  </div>
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                    <Receipt className="w-6 h-6 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Pending Payments Section */}
          {pendingInvoices.length > 0 && (
            <Card className="border-orange-200 bg-orange-50/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-orange-700">
                  <Receipt className="w-5 h-5" />
                  Pending Payment Approvals
                </CardTitle>
                <CardDescription>
                  These invoices are awaiting payment verification. Click "Approve" to activate the subscription.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pendingInvoices.slice(0, 5).map((invoice) => (
                    <div 
                      key={invoice.id} 
                      className="flex items-center justify-between p-4 bg-white rounded-lg border border-orange-200"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                          <Banknote className="w-5 h-5 text-orange-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{invoice.tenant_name}</p>
                          <p className="text-sm text-slate-500">
                            {invoice.invoice_number} • {new Date(invoice.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-semibold text-lg">{formatCurrency(invoice.total_amount)}</p>
                          <Badge className="bg-orange-100 text-orange-700">Awaiting Verification</Badge>
                        </div>
                        <Button 
                          className="bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => openApproveDialog(invoice)}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                      </div>
                    </div>
                  ))}
                  {pendingInvoices.length > 5 && (
                    <p className="text-center text-sm text-slate-500 pt-2">
                      +{pendingInvoices.length - 5} more pending invoices
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Plans Summary */}
          <Card>
            <CardHeader>
              <CardTitle>Active Plans</CardTitle>
              <CardDescription>Currently available subscription plans</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                {plans.filter(p => p.is_active).map((plan) => (
                  <div key={plan.id} className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold">{plan.name}</h3>
                      <Badge variant="outline">{plan.sort_order}</Badge>
                    </div>
                    <p className="text-2xl font-bold text-slate-900">
                      {formatCurrency(plan.price_monthly)}<span className="text-sm font-normal text-slate-500">/mo</span>
                    </p>
                    <p className="text-sm text-slate-500 mt-1">
                      {plan.features?.length || 0} features enabled
                    </p>
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" variant="outline" onClick={() => openEditPlanDialog(plan)}>
                        <Edit className="w-3 h-3 mr-1" />Edit
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Recent Subscriptions */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Trial Signups</CardTitle>
            </CardHeader>
            <CardContent>
              {tenantSubscriptions.filter(t => t.status === 'trial').slice(0, 5).length === 0 ? (
                <p className="text-slate-500 text-center py-4">No active trials</p>
              ) : (
                <div className="space-y-3">
                  {tenantSubscriptions.filter(t => t.status === 'trial').slice(0, 5).map((tenant) => (
                    <div key={tenant.tenant_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium">{tenant.company_name}</p>
                        <p className="text-sm text-slate-500">{tenant.plan_name} Trial</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm">Ends: {tenant.trial_ends_at ? new Date(tenant.trial_ends_at).toLocaleDateString() : 'N/A'}</p>
                        {getStatusBadge(tenant.status)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Plans Tab */}
        <TabsContent value="plans" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Subscription Plans</CardTitle>
                <CardDescription>Manage your pricing plans and features</CardDescription>
              </div>
              <Button onClick={openCreatePlanDialog}>
                <Plus className="w-4 h-4 mr-2" />
                New Plan
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {plans.map((plan) => (
                  <div 
                    key={plan.id} 
                    className={`p-4 border rounded-lg ${!plan.is_active ? 'opacity-60 bg-slate-50' : ''}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold">{plan.name}</h3>
                          {!plan.is_active && <Badge variant="secondary">Inactive</Badge>}
                          {plan.is_public && <Badge variant="outline">Public</Badge>}
                          {plan.trial_available && <Badge className="bg-blue-100 text-blue-700">Trial Available</Badge>}
                        </div>
                        <p className="text-slate-500 text-sm mb-3">{plan.description}</p>
                        <div className="flex items-center gap-6 text-sm">
                          <div>
                            <span className="text-slate-500">Monthly:</span>{' '}
                            <span className="font-semibold">{formatCurrency(plan.price_monthly)}</span>
                          </div>
                          <div>
                            <span className="text-slate-500">Annual:</span>{' '}
                            <span className="font-semibold">{formatCurrency(plan.price_annual)}</span>
                          </div>
                          <div>
                            <span className="text-slate-500">Features:</span>{' '}
                            <span className="font-semibold">{plan.features?.length || 0}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => openEditPlanDialog(plan)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDeletePlan(plan.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Features Preview */}
                    {plan.features?.length > 0 && (
                      <div className="mt-4 pt-4 border-t">
                        <p className="text-sm font-medium text-slate-700 mb-2">Included Features:</p>
                        <div className="flex flex-wrap gap-2">
                          {plan.features.map((f, idx) => (
                            <Badge key={idx} variant="secondary" className="font-normal">
                              {f.name || f.feature_key}
                              {f.limit_value !== null && f.limit_value !== -1 && ` (${f.limit_value})`}
                              {f.limit_value === -1 && ' (∞)'}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tenants Tab */}
        <TabsContent value="tenants" className="space-y-4 mt-6">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search company, email..."
                      className="pl-9"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <select
                  className="h-10 px-3 rounded-md border border-slate-200 bg-white text-sm min-w-[150px]"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="trial">Trial</option>
                  <option value="pending">Pending Payment</option>
                  <option value="past_due">Past Due</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="no_subscription">No Subscription</option>
                </select>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('');
                  }}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reset
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Table */}
          <Card>
            <CardContent className="p-0">
              {loadingTable ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : filteredTenants.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Building2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No tenants found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-left p-4 font-medium text-slate-600">Tenant</th>
                        <th className="text-left p-4 font-medium text-slate-600">Plan</th>
                        <th className="text-left p-4 font-medium text-slate-600">Status</th>
                        <th className="text-left p-4 font-medium text-slate-600">Usage</th>
                        <th className="text-left p-4 font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTenants.map((tenant) => (
                        <tr key={tenant.tenant_id} className="border-b hover:bg-slate-50">
                          <td className="p-4">
                            <div>
                              <p className="font-medium text-slate-900">{tenant.company_name}</p>
                              <p className="text-sm text-slate-500">{tenant.email}</p>
                            </div>
                          </td>
                          <td className="p-4">
                            <p className="font-medium">{tenant.plan_name || 'No Plan'}</p>
                            {tenant.billing_cycle && (
                              <p className="text-sm text-slate-500">{tenant.billing_cycle}</p>
                            )}
                          </td>
                          <td className="p-4">
                            {getStatusBadge(tenant.status)}
                            {tenant.status === 'trial' && tenant.trial_ends_at && (
                              <p className="text-xs text-slate-500 mt-1">
                                Ends: {new Date(tenant.trial_ends_at).toLocaleDateString()}
                              </p>
                            )}
                          </td>
                          <td className="p-4 text-sm">
                            <div className="space-y-1">
                              <p>Quotes: {tenant.usage?.quotes_this_month || 0}/mo</p>
                              <p>Hotels: {tenant.usage?.hotel_count || 0}</p>
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" onClick={() => openTenantDetail(tenant)}>
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => openInvoiceDialog(tenant)}>
                                <CreditCard className="w-4 h-4" />
                              </Button>
                              {tenant.status === 'pending' && (
                                <Button 
                                  size="sm" 
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                  onClick={() => openApproveForTenant(tenant)}
                                >
                                  <Check className="w-4 h-4 mr-1" />
                                  Approve
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Plan Create/Edit Dialog */}
      <Dialog open={showPlanDialog} onOpenChange={setShowPlanDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPlan ? 'Edit Plan' : 'Create Plan'}</DialogTitle>
            <DialogDescription>Configure plan details and features</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Plan Name</Label>
                <Input
                  value={planForm.name}
                  onChange={(e) => setPlanForm({...planForm, name: e.target.value})}
                  placeholder="e.g., Professional"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Slug (unique ID)</Label>
                <Input
                  value={planForm.slug}
                  onChange={(e) => setPlanForm({...planForm, slug: e.target.value.toLowerCase().replace(/\s/g, '_')})}
                  placeholder="e.g., professional"
                  className="mt-1"
                  disabled={!!editingPlan}
                />
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={planForm.description}
                onChange={(e) => setPlanForm({...planForm, description: e.target.value})}
                placeholder="Brief description of the plan"
                className="mt-1"
                rows={2}
              />
            </div>

            {/* Pricing */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Monthly Price (₹)</Label>
                <Input
                  type="number"
                  value={planForm.price_monthly}
                  onChange={(e) => setPlanForm({...planForm, price_monthly: e.target.value})}
                  placeholder="999"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Annual Price (₹)</Label>
                <Input
                  type="number"
                  value={planForm.price_annual}
                  onChange={(e) => setPlanForm({...planForm, price_annual: e.target.value})}
                  placeholder="9999"
                  className="mt-1"
                />
              </div>
            </div>

            {/* Toggles */}
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  checked={planForm.trial_available}
                  onCheckedChange={(checked) => setPlanForm({...planForm, trial_available: checked})}
                />
                <Label>Trial Available</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={planForm.is_public}
                  onCheckedChange={(checked) => setPlanForm({...planForm, is_public: checked})}
                />
                <Label>Public</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={planForm.is_active}
                  onCheckedChange={(checked) => setPlanForm({...planForm, is_active: checked})}
                />
                <Label>Active</Label>
              </div>
            </div>

            <div>
              <Label>Sort Order</Label>
              <Input
                type="number"
                value={planForm.sort_order}
                onChange={(e) => setPlanForm({...planForm, sort_order: parseInt(e.target.value) || 1})}
                className="mt-1 w-24"
              />
            </div>

            <Separator />

            {/* Features */}
            <div>
              <Label className="text-base font-semibold">Features</Label>
              <p className="text-sm text-slate-500 mb-4">Select features to include in this plan</p>
              
              <div className="space-y-6">
                {getFeaturesByCategory().map((categoryGroup) => (
                  <div key={categoryGroup.category}>
                    <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">
                      {categoryGroup.name}
                    </h4>
                    <div className="grid gap-3">
                      {categoryGroup.features.map((feature) => {
                        const Icon = feature.icon;
                        const isEnabled = planForm.features[feature.key]?.is_enabled;
                        const limitValue = planForm.features[feature.key]?.limit_value;
                        
                        return (
                          <div 
                            key={feature.key}
                            className={`p-3 border rounded-lg transition-colors ${isEnabled ? 'border-indigo-200 bg-indigo-50' : 'border-slate-200'}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded flex items-center justify-center ${isEnabled ? 'bg-indigo-100' : 'bg-slate-100'}`}>
                                  <Icon className={`w-4 h-4 ${isEnabled ? 'text-indigo-600' : 'text-slate-400'}`} />
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <p className={`font-medium ${isEnabled ? 'text-indigo-900' : 'text-slate-700'}`}>
                                      {feature.name}
                                    </p>
                                    {feature.isNew && (
                                      <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase bg-emerald-100 text-emerald-700 rounded">
                                        NEW
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-slate-500">{feature.key}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                {feature.hasLimit && isEnabled && (
                                  <div className="flex items-center gap-2">
                                    <Input
                                      type="number"
                                      value={limitValue === -1 ? '' : (limitValue || '')}
                                      onChange={(e) => updateFeatureLimit(feature.key, e.target.value)}
                                      placeholder="Limit"
                                      className="w-20 h-8 text-sm"
                                    />
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 px-2 text-xs"
                                      onClick={() => updateFeatureLimit(feature.key, '-1')}
                                    >
                                      ∞
                                    </Button>
                                  </div>
                                )}
                                <Switch
                                  checked={isEnabled}
                                  onCheckedChange={() => toggleFeature(feature.key, feature.hasLimit)}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPlanDialog(false)}>Cancel</Button>
            <Button onClick={handleSavePlan}>
              {editingPlan ? 'Update Plan' : 'Create Plan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Dialog */}
      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Invoice</DialogTitle>
            <DialogDescription>
              Generate an invoice for {selectedTenant?.company_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Plan</Label>
              <select
                className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
                value={invoiceForm.plan_id}
                onChange={(e) => setInvoiceForm({...invoiceForm, plan_id: e.target.value})}
              >
                <option value="">Select plan...</option>
                {plans.filter(p => p.is_active).map((plan) => (
                  <option key={plan.id} value={plan.id}>{plan.name} - {formatCurrency(plan.price_monthly)}/mo</option>
                ))}
              </select>
            </div>
            
            <div>
              <Label>Billing Cycle</Label>
              <select
                className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
                value={invoiceForm.billing_cycle}
                onChange={(e) => setInvoiceForm({...invoiceForm, billing_cycle: e.target.value})}
              >
                <option value="monthly">Monthly</option>
                <option value="annual">Annual</option>
              </select>
            </div>

            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={invoiceForm.notes}
                onChange={(e) => setInvoiceForm({...invoiceForm, notes: e.target.value})}
                placeholder="Any additional notes..."
                className="mt-1"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvoiceDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateInvoice}>Generate Invoice</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tenant Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Tenant Details</DialogTitle>
          </DialogHeader>
          
          {selectedTenant && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="font-medium text-lg">{selectedTenant.company_name}</p>
                <p className="text-sm text-slate-500">{selectedTenant.email}</p>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Plan</p>
                  <p className="font-medium">{selectedTenant.plan_name || 'No Plan'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Status</p>
                  {getStatusBadge(selectedTenant.status)}
                </div>
                <div>
                  <p className="text-slate-500">Billing Cycle</p>
                  <p className="font-medium capitalize">{selectedTenant.billing_cycle || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Trial Ends</p>
                  <p className="font-medium">
                    {selectedTenant.trial_ends_at ? new Date(selectedTenant.trial_ends_at).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>

              <Separator />

              <div>
                <p className="font-medium mb-2">Usage Stats</p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="p-3 bg-slate-50 rounded">
                    <p className="text-slate-500">Quotes This Month</p>
                    <p className="text-lg font-semibold">{selectedTenant.usage?.quotes_this_month || 0}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded">
                    <p className="text-slate-500">Hotels</p>
                    <p className="text-lg font-semibold">{selectedTenant.usage?.hotel_count || 0}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded">
                    <p className="text-slate-500">B2B Agents</p>
                    <p className="text-lg font-semibold">{selectedTenant.usage?.b2b_agents || 0}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded">
                    <p className="text-slate-500">Staff Users</p>
                    <p className="text-lg font-semibold">{selectedTenant.usage?.staff_users || 0}</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-2 pt-4">
                <Button className="flex-1" onClick={() => {
                  setShowDetailDialog(false);
                  openInvoiceDialog(selectedTenant);
                }}>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Create Invoice
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Approve Payment Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              Approve Payment
            </DialogTitle>
            <DialogDescription>
              Verify and approve this offline payment to activate the subscription.
            </DialogDescription>
          </DialogHeader>
          
          {selectedInvoice && (
            <div className="space-y-4 py-4">
              {/* Tenant & Invoice Info */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-500">Tenant</span>
                  <span className="font-medium">{selectedInvoice.tenant?.company_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Invoice</span>
                  <span className="font-medium">{selectedInvoice.invoice_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Invoice Date</span>
                  <span>{selectedInvoice.invoice_date ? new Date(selectedInvoice.invoice_date).toLocaleDateString() : 'N/A'}</span>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <span className="text-slate-500">Subtotal</span>
                  <span>{formatCurrency(selectedInvoice.subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">GST (18%)</span>
                  <span>{formatCurrency(selectedInvoice.tax_amount)}</span>
                </div>
                <div className="flex justify-between text-lg font-semibold pt-2 border-t">
                  <span>Total Amount</span>
                  <span className="text-green-600">{formatCurrency(selectedInvoice.total_amount)}</span>
                </div>
              </div>

              {/* Payment Details from Notes */}
              {selectedInvoice.notes && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="font-medium text-blue-800 mb-2 flex items-center gap-2">
                    <Banknote className="w-4 h-4" />
                    Payment Details (from Tenant)
                  </p>
                  <pre className="text-sm text-blue-700 whitespace-pre-wrap font-mono">
                    {selectedInvoice.notes}
                  </pre>
                </div>
              )}

              {/* Warning */}
              <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200 text-sm text-yellow-800">
                <p className="font-medium">Important:</p>
                <p>Please verify the payment has been received in your bank account before approving.</p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-green-600 hover:bg-green-700"
              onClick={handleApprovePayment}
              disabled={approvingPayment}
            >
              {approvingPayment ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</>
              ) : (
                <><CheckCircle2 className="w-4 h-4 mr-2" />Approve & Activate</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubscriptionManagement;
